'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Heart, Download, Eye, Play, Clock, CheckCircle, Bookmark } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import { toggleSavedClip, isClipSaved } from '@/lib/clipStore';
import CategoryIcon from '@/components/ui/CategoryIcon';

export interface ClipData {
  id: string;
  title: string;
  uploader: string;
  uploaderAvatar: string;
  uploaderUsername?: string;
  verified: boolean;
  premium: boolean;
  thumbnail: string;
  videoUrl?: string;
  duration: string;
  resolution: string;
  fps: number;
  licenseType: 'CC0' | 'CC BY' | 'CC BY-SA' | 'Personal';
  likes: number;
  downloads: number;
  views: number;
  tags: string[];
  uploadedAt: string;
  categories: string[];
}

interface ClipCardProps {
  clip: ClipData;
  variant?: 'default' | 'featured';
}

function formatCount(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
}

const licenseConfig: Record<string, { label: string; className: string }> = {
  CC0: { label: 'CC0 Free', className: 'badge-license badge-cc0' },
  'CC BY': { label: 'CC BY', className: 'badge-license badge-ccby' },
  'CC BY-SA': { label: 'CC BY-SA', className: 'badge-license badge-ccby' },
  Personal: { label: 'Personal', className: 'badge-license badge-personal' },
};

export default function ClipCard({ clip, variant = 'default' }: ClipCardProps) {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Sync saved state from localStorage on mount
  useEffect(() => {
    setSaved(isClipSaved(clip.id));
  }, [clip.id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (hovered) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      video.pause();
      video.currentTime = 0;
      setVideoReady(false);
    }
  }, [hovered]);

  function handleSave(e: React.MouseEvent) {
    e.stopPropagation();
    const nowSaved = toggleSavedClip(clip);
    setSaved(nowSaved);
  }

  async function handleDownload(e: React.MouseEvent) {
    e.stopPropagation();
    if (!clip.videoUrl || downloading) return;
    setDownloading(true);
    try {
      const response = await fetch(clip.videoUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = clip.videoUrl.split('.').pop()?.split('?')[0] || 'mp4';
      a.download = `${clip.title.replace(/[^a-z0-9]/gi, '_')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // fallback: open in new tab
      window.open(clip.videoUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  }

  const license = licenseConfig[clip.licenseType];

  return (
    <div
      className="clip-card-hover rounded-xl overflow-hidden cursor-pointer group"
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Thumbnail / Preview */}
      <div
        className="relative overflow-hidden"
        style={{ aspectRatio: '16/9' }}
      >
        <AppImage
          src={clip.thumbnail}
          alt={`${clip.title} — clip thumbnail by ${clip.uploader}`}
          fill
          className="object-cover transition-transform duration-300 group-hover:scale-105"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />

        {/* Video preview — plays on hover when videoUrl is available */}
        {clip.videoUrl && (
          <video
            ref={videoRef}
            src={clip.videoUrl}
            muted
            playsInline
            loop
            preload="none"
            onCanPlay={() => setVideoReady(true)}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
            style={{ opacity: hovered && videoReady ? 1 : 0 }}
          />
        )}

        {/* Overlay on hover */}
        <div
          className="absolute inset-0 flex items-center justify-center transition-opacity duration-200"
          style={{
            background: 'rgba(0,0,0,0.45)',
            opacity: hovered && !(clip.videoUrl && videoReady) ? 1 : 0,
          }}
        >
          <span
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(108, 99, 255, 0.9)' }}
          >
            <Play size={20} fill="white" className="text-white ml-0.5" />
          </span>
        </div>

        {/* Duration badge */}
        <span
          className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold font-mono"
          style={{ background: 'rgba(0,0,0,0.75)', color: 'white' }}
        >
          <Clock size={10} />
          {clip.duration}
        </span>

        {/* Resolution + FPS badges */}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <span className="badge-resolution">{clip.resolution}</span>
          <span className="badge-fps">{clip.fps}fps</span>
        </div>

        {/* License badge */}
        <div className="absolute top-2 right-2">
          <span className={license.className}>{license.label}</span>
        </div>

        {/* Save button — always visible, highlighted when saved */}
        <button
          onClick={handleSave}
          className="absolute bottom-2 left-2 w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 hover:scale-110"
          style={{
            background: saved ? 'rgba(108, 99, 255, 0.9)' : 'rgba(0,0,0,0.6)',
            color: saved ? 'white' : 'rgba(255,255,255,0.7)',
          }}
          title={saved ? 'Remove from saved' : 'Save to collection'}
        >
          <Bookmark size={12} fill={saved ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Card Body */}
      <div className="p-3">
        {/* Title */}
        <h3
          className="font-semibold text-sm leading-snug mb-2 line-clamp-2 transition-colors duration-150 group-hover:text-primary"
          style={{ color: 'var(--foreground)' }}
        >
          {clip.title}
        </h3>

        {/* Uploader */}
        <div className="flex items-center gap-2 mb-3">
          <Link
            href={`/creator/${encodeURIComponent(clip.uploader.toLowerCase().replace(/\s+/g, '.'))}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity duration-150"
          >
            <div className="relative w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
              <AppImage
                src={clip.uploaderAvatar}
                alt={`${clip.uploader} profile picture`}
                fill
                className="object-cover"
                sizes="20px"
              />
            </div>
            <span className="text-xs font-medium truncate" style={{ color: 'var(--muted-foreground)' }}>
              {clip.uploader}
            </span>
          </Link>
          {clip.verified && (
            <CheckCircle size={12} style={{ color: 'var(--verified)', flexShrink: 0 }} />
          )}
          {clip.premium && (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm"
              style={{ background: 'rgba(245, 158, 11, 0.15)', color: 'var(--premium)', border: '1px solid rgba(245, 158, 11, 0.3)' }}
            >
              PRO
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={(e) => { e.stopPropagation(); setLiked(!liked); }}
            className="flex items-center gap-1 transition-all duration-150 hover:scale-105"
            style={{ color: liked ? '#EF4444' : 'var(--muted-foreground)' }}
          >
            <Heart size={13} fill={liked ? 'currentColor' : 'none'} />
            <span className="text-xs tabular-nums font-medium">{formatCount(clip.likes + (liked ? 1 : 0))}</span>
          </button>
          <button
            onClick={handleDownload}
            disabled={!clip.videoUrl || downloading}
            className="flex items-center gap-1 transition-all duration-150 hover:scale-105 disabled:cursor-default"
            style={{
              color: clip.videoUrl
                ? downloading
                  ? 'var(--primary)'
                  : 'var(--muted-foreground)' :'var(--muted-foreground)',
            }}
            title={clip.videoUrl ? 'Download clip' : 'No download available'}
          >
            <Download size={13} className={downloading ? 'animate-bounce' : ''} />
            <span className="text-xs tabular-nums font-medium">{formatCount(clip.downloads)}</span>
          </button>
          <div className="flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
            <Eye size={13} />
            <span className="text-xs tabular-nums font-medium">{formatCount(clip.views)}</span>
          </div>
          <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>
            {clip.uploadedAt}
          </span>
        </div>

        {/* Tags + Categories */}
        <div className="flex flex-wrap gap-1">
          {/* Category badges with icons */}
          {clip.categories?.slice(0, 2).map((cat) => (
            <span
              key={`cat-${clip.id}-${cat}`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md"
              style={{
                background: 'rgba(108, 99, 255, 0.08)',
                color: 'var(--primary)',
                border: '1px solid rgba(108, 99, 255, 0.15)',
              }}
            >
              <CategoryIcon category={cat} size={10} />
              {cat}
            </span>
          ))}
          {clip.tags.slice(0, 2).map((tag) => (
            <span
              key={`tag-${clip.id}-${tag}`}
              className="text-[10px] font-medium px-2 py-0.5 rounded-md"
              style={{
                background: 'rgba(108, 99, 255, 0.1)',
                color: 'var(--primary)',
                border: '1px solid rgba(108, 99, 255, 0.2)',
              }}
            >
              #{tag}
            </span>
          ))}
          {clip.tags.length > 2 && (
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-md"
              style={{ color: 'var(--muted-foreground)', background: 'var(--muted)' }}
            >
              +{clip.tags.length - 2}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}